/**
 * Image Size → Strapi Format Configuration
 *
 * We deliberately avoid runtime viewport/DPR calculations and browser-driven
 * srcset/sizes in favor of a single deterministic Strapi format per size category.
 *
 * WHY:
 * Our preload strategy requires that the URL resolved at preload time is identical
 * to the URL used at render time. If the browser picks the format (via srcset), we
 * can't guarantee a cache hit — the preloader might fetch "medium" while the browser
 * decides to render "large", causing a redundant download and a visible loading delay.
 *
 * By controlling format selection in JS with a shared config, the PreloadManager and
 * the StrapiMedia component always resolve to the exact same URL. One code path,
 * guaranteed cache hit.
 *
 * CURRENT VALUES:
 * Chosen for a ~360px CSS phone-frame layout at 2x DPR (the vast majority of our
 * users). "large" (1000px) comfortably covers 2x and is close enough to 3x (1080px)
 * that the quality difference is imperceptible. 1x displays get a slightly oversized
 * image — negligible bandwidth cost given our audience.
 *
 * FUTURE OPTIMIZATION:
 * If we need to support significantly different display densities or layouts, change
 * the values below to a function that computes the format key based on devicePixelRatio
 * or viewport width. Neither the PreloadManager nor StrapiMedia need to change — they
 * both call resolveImageUrl() and stay in sync automatically.
 */

import { getStrapiMediaUrl } from "@/utils/strapiUtils";
import { StrapiAsset } from "@/types/api";

export type ImageSize = "sm" | "md" | "lg";

const IMAGE_FORMAT_CONFIG: Record<ImageSize, string> = {
    /** Small — icons, thumbnails (e.g. meme icons, achievement icons at 80×80) */
    sm: "thumbnail",
    /** Medium — mid-sized images (e.g. educational images at ~80vw) */
    md: "medium",
    /** Large — full-width images (e.g. room backgrounds, item assets) */
    lg: "large",
};

/**
 * Returns the Strapi format key for a given image size.
 * Central point of control — change this to a function for DPR-aware logic later.
 */
export function getFormatKey(size: ImageSize): string {
    return IMAGE_FORMAT_CONFIG[size];
}

/**
 * Resolves a StrapiAsset to a concrete URL using the size-based format.
 * Falls back to the original URL if the desired format doesn't exist on the image
 * (e.g. SVGs which have no format variants).
 *
 * This is the ONLY function that should be used to resolve image URLs for both
 * preloading and rendering — using it in both places guarantees cache hits.
 */
export function resolveImageUrl(image: StrapiAsset, size: ImageSize): string {
    const formatKey = getFormatKey(size);
    const format = image.formats?.[formatKey];
    const url = format?.url ?? image.url;

    return getStrapiMediaUrl(url, image.updatedAt);
}

/**
 * Resolves a video StrapiAsset to its full URL.
 * Videos have no format variants — just resolve the original URL.
 */
export function resolveVideoUrl(media: StrapiAsset): string {
    return getStrapiMediaUrl(media.url, media.updatedAt);
}